import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import { getApiBaseUrl } from "@synthesis/shared-services";
import { IconSearch } from "@synthesis/shared-icons";
import { ScrollArea } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { ListItem, ModalSearch } from "./local-components";

const HELP_BASE = "/help";
const STORAGE_KEY_PREFIX = "helpPageActiveHeading_";

type HelpSubsection = {
  title: string;
  file: string;
};

type HelpSection = {
  title: string;
  subsections?: HelpSubsection[];
};

const VideoPlayer = ({ src }: { src: string }) => {
  const videoUrl = React.useMemo(() => {
    return `${getApiBaseUrl()}/api/file/api/document/knowledge-base/video/${src}`;
  }, [src]);

  return <video controls src={videoUrl} style={{ maxWidth: "100%" }} />;
};

const handleHeadingClick = (anchorId: string) => {
  const element = document.getElementById(anchorId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

function extractHeadings(content: string) {
  if (content === "Loading" || content === "Ошибка загрузки" || content === "Руководство ещё не загружено") {
    return [];
  }

  const regex = /^(#{1,6})\s+(.*)/gm;
  const headings: string[] = [];
  let match;

  while ((match = regex.exec(content)) !== null) {
    const cleanText = (match[2] ?? "")
      .replace(/(\*\*|__|\*|_)/g, "")
      .replace(/<[^>]+>/g, "")
      .replace(/\s*#+$/, "")
      .trim();

    if (cleanText) {
      headings.push(cleanText);
    }
  }

  return headings;
}

const extractText = (node: React.ReactNode): string => {
  if (typeof node === "string") {
    return node;
  }

  if (Array.isArray(node)) {
    return node.map(extractText).join("");
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extractText(node.props.children);
  }

  return "";
};

const renderHeading = (children: React.ReactNode): string => extractText(children).trim();

const HelpMarkdownMark = (props: React.ComponentProps<"mark">) => (
  <mark id={props.id} className="bg-tertiary-95">
    {props.children}
  </mark>
);

type HelpMarkdownHeadingProps = {
  level: 1 | 2 | 3 | 4 | 5;
  children: React.ReactNode;
};

const HelpMarkdownHeading = ({ level, children }: HelpMarkdownHeadingProps) => {
  const anchorId = renderHeading(children);

  switch (level) {
    case 1:
      return <h1 id={anchorId}>{children}</h1>;
    case 2:
      return <h2 id={anchorId}>{children}</h2>;
    case 3:
      return <h3 id={anchorId}>{children}</h3>;
    case 4:
      return <h4 id={anchorId}>{children}</h4>;
    case 5:
      return <h5 id={anchorId}>{children}</h5>;
  }
};

const HelpMarkdownH1 = ({ children }: React.ComponentProps<"h1">) => <HelpMarkdownHeading level={1}>{children}</HelpMarkdownHeading>;
const HelpMarkdownH2 = ({ children }: React.ComponentProps<"h2">) => <HelpMarkdownHeading level={2}>{children}</HelpMarkdownHeading>;
const HelpMarkdownH3 = ({ children }: React.ComponentProps<"h3">) => <HelpMarkdownHeading level={3}>{children}</HelpMarkdownHeading>;
const HelpMarkdownH4 = ({ children }: React.ComponentProps<"h4">) => <HelpMarkdownHeading level={4}>{children}</HelpMarkdownHeading>;
const HelpMarkdownH5 = ({ children }: React.ComponentProps<"h5">) => <HelpMarkdownHeading level={5}>{children}</HelpMarkdownHeading>;

const HelpFilePathContext = React.createContext("");

const HelpMarkdownImage = ({ src, alt, title, ...props }: React.ComponentProps<"img">) => {
  const currentFilePath = React.useContext(HelpFilePathContext);
  if (!src) return null;

  if (alt === "VIDEO") return <VideoPlayer src={src} />;

  let newSrc = src;

  if (!/^https?:\/\//.test(src) && !src.startsWith("/")) {
    newSrc = `${HELP_BASE}/${currentFilePath.replace(/\/[^/]+$/, "")}/${src}`;
  }

  if (title === "inline") {
    return (
      <img
        src={newSrc}
        alt={alt}
        {...props}
        style={{
          verticalAlign: "middle",
          height: "1em",
          display: "inline-block",
          margin: "0 0.2em",
        }}
      />
    );
  }

  return <img src={newSrc} alt={alt} title={title} {...props} />;
};

const helpMarkdownComponents: Components = {
  mark: HelpMarkdownMark,
  h1: HelpMarkdownH1,
  h2: HelpMarkdownH2,
  h3: HelpMarkdownH3,
  h4: HelpMarkdownH4,
  h5: HelpMarkdownH5,
  img: HelpMarkdownImage,
};

export const HelpPage = () => {
  const [activeSideBarItem, setActiveSideBarItem] = useState("");
  const [mdContent, setMdContent] = useState("");
  const [activeHeading, setActiveHeading] = useState("");
  const [currentFilePath, setCurrentFilePath] = useState("");
  const scrollAreaRef = React.useRef<HTMLDivElement | null>(null);
  const skipScrollOnMdChangeRef = React.useRef(false);
  const programmaticScrollRef = React.useRef(false);
  const activeHeadingRef = React.useRef(activeHeading);
  const headingsRef = React.useRef<string[]>([]);
  const activeSideBarItemRef = React.useRef(activeSideBarItem);
  const initializedContentKeyRef = React.useRef("");

  activeHeadingRef.current = activeHeading;
  activeSideBarItemRef.current = activeSideBarItem;

  const updateActiveHeading = React.useCallback((heading: string) => {
    setActiveHeading((current) => (current === heading ? current : heading));

    const sidebarItem = activeSideBarItemRef.current;
    if (sidebarItem) {
      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${sidebarItem}`, heading);
    }
  }, []);

  const { data: versionData } = useQuery({
    queryKey: ["help-version"],
    queryFn: async () => {
      const res = await fetch(`${HELP_BASE}/version.json`);
      if (!res.ok) throw new Error("Failed to load version");
      return (await res.json()) as { version?: string };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const version = versionData?.version || "default";

  const { data: sectionsData = [] } = useQuery({
    queryKey: ["help-sections", version],
    queryFn: async () => {
      const res = await fetch(`${HELP_BASE}/sections.json`);
      if (!res.ok) throw new Error("Failed to load sections");
      const json = (await res.json()) as HelpSection;
      return [json];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled: !!versionData,
  });

  const processVideos = React.useCallback(
    (mdData: string) => mdData.replace(/<video[^>]*src="([^"]+)"[^>]*><\/video>/g, (_, src) => `![VIDEO](${src})`),
    [],
  );

  const handleItemClick = React.useCallback(
    async (itemTitle: string, filePath: string, anchorId?: string, position?: number, query?: string) => {
      setActiveSideBarItem(itemTitle);
      setMdContent("Loading");

      if (!filePath) {
        setMdContent("Руководство ещё не загружено");
        return;
      }

      try {
        let mdData = await (await fetch(`${HELP_BASE}/${filePath}`)).text();
        mdData = processVideos(mdData);

        if (anchorId && position !== undefined && query) {
          skipScrollOnMdChangeRef.current = true;
          const before = mdData.slice(0, position);
          const match = mdData.slice(position, position + query.length);
          const after = mdData.slice(position + query.length);

          mdData = `${before}<mark id="${anchorId}" style="background-color: yellow;">${match}</mark>${after}`;
        }

        setMdContent(mdData);
        setCurrentFilePath(filePath);
      } catch (error) {
        logger.error(error);
        setMdContent("Ошибка загрузки");
      }
    },
    [processVideos],
  );

  const onHeadingClick = React.useCallback(
    (anchorId: string) => {
      updateActiveHeading(anchorId);
      programmaticScrollRef.current = true;
      handleHeadingClick(anchorId);

      window.setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 700);
    },
    [updateActiveHeading],
  );

  const headings = React.useMemo(() => extractHeadings(mdContent), [mdContent]);
  headingsRef.current = headings;

  React.useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    let frameId = 0;

    const handleScroll = () => {
      if (programmaticScrollRef.current) return;

      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        if (programmaticScrollRef.current || !scrollAreaRef.current) return;

        const currentActiveHeading = headingsRef.current.find((heading) => {
          const element = document.getElementById(heading);
          if (!element) return false;

          const rect = element.getBoundingClientRect();
          return rect.top >= 0 && rect.top <= scrollAreaRef.current!.clientHeight;
        });

        if (currentActiveHeading && currentActiveHeading !== activeHeadingRef.current) {
          updateActiveHeading(currentActiveHeading);
        }
      });
    };

    scrollArea.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      scrollArea.removeEventListener("scroll", handleScroll);
    };
  }, [updateActiveHeading]);

  React.useEffect(() => {
    if (skipScrollOnMdChangeRef.current) {
      skipScrollOnMdChangeRef.current = false;
      return;
    }

    if (!mdContent || mdContent === "Loading") return;

    const contentKey = `${activeSideBarItem}:${mdContent.length}`;
    if (initializedContentKeyRef.current === contentKey) return;
    initializedContentKeyRef.current = contentKey;

    const savedHeading = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${activeSideBarItem}`);
    const nextHeading = savedHeading || headingsRef.current[0];
    if (!nextHeading) return;

    updateActiveHeading(nextHeading);

    programmaticScrollRef.current = true;
    window.setTimeout(() => {
      const element = document.getElementById(nextHeading);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 700);
    }, 10);
  }, [mdContent, activeSideBarItem, updateActiveHeading]);

  React.useEffect(() => {
    if (!sectionsData.length || activeSideBarItem) return;

    const firstSubsection = sectionsData[0]?.subsections?.[0];
    if (!firstSubsection) return;

    void handleItemClick(firstSubsection.title, firstSubsection.file);
  }, [sectionsData, activeSideBarItem, handleItemClick]);

  return (
    <div className="flex w-full gap-6 bg-white px-6 py-4">
      <div className="flex flex-col justify-between gap-4">
        <ScrollArea className="h-[84vh] w-[284px] gap-4 rounded-xl px-3 py-2">
          {sectionsData.map((item) => (
            <React.Fragment key={item.title}>
              <ModalSearch
                sectionsData={sectionsData}
                handleItemClick={handleItemClick}
                trigger={
                  <div
                    tabIndex={0}
                    className="focus-within:(outline outline-system-info-70) bg-neutrals-95 flex h-10 items-center rounded-[.875rem] text-gray-900 outline-2 -outline-offset-1"
                  >
                    <div className="text-neutrals-60 flex items-center justify-center gap-3 px-3">
                      <IconSearch className="size-4" />
                      <span>Поиск...</span>
                    </div>
                  </div>
                }
              />

              <div className="subtitle-lg border-neutrals-90 text-neutrals-30 my-2 border-b-2 px-3 py-2">{item.title}</div>
              {item.subsections?.map((child, childIndex) => (
                <React.Fragment key={child.file}>
                  <ListItem
                    title={child.title}
                    fileMetaId={child.file}
                    index={childIndex}
                    handleItemClick={handleItemClick}
                    isActive={activeSideBarItem === child.title}
                    rightBar
                  />

                  {activeSideBarItem === child.title && headings.length > 0 && (
                    <div className="mb-4">
                      <ScrollArea className="bg-neutrals-99 rounded-b-lg">
                        <ul className="bg-neutrals-99 p-2">
                          {headings.map((heading) => (
                            <ListItem key={heading} title={heading} isActive={activeHeading === heading} handleHeadingClick={onHeadingClick} />
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </ScrollArea>
      </div>
      <ScrollArea className="h-[84vh] w-full grow" ref={scrollAreaRef}>
        {mdContent === "Loading" ? (
          <div className="bg-neutrals-95 h-[84vh] w-full animate-pulse rounded-xl" />
        ) : (
          <div className="prose" style={{ maxWidth: "none" }}>
            <HelpFilePathContext.Provider value={currentFilePath}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={helpMarkdownComponents}>
                {mdContent}
              </ReactMarkdown>
            </HelpFilePathContext.Provider>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
